/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
import { Injectable, PipeTransform } from '@angular/core';
import { getFirebaseBackend } from 'src/app/authUtils';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { ListForecastMonth } from '../../../core/models/forecast.models';
import { DecimalPipe } from '@angular/common';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import {
	SortColumn,
	SortDirection,
} from '../listjs-sortable-forecast-list.directive';
import { ForecastService } from 'src/app/services/forecast/forecast.service';

interface SearchResult {
	list: ListForecastMonth[];
	total: number;
}

export interface State {
	page: number;
	pageSize: number;
	searchTerm: string;
	sortColumn: SortColumn;
	sortDirection: SortDirection;
	startIndex: number;
	endIndex: number;
	totalRecords: number;
}
const compare = (v1: string | number, v2: string | number) =>
	v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

function sort(
	list: ListForecastMonth[],
	column: SortColumn,
	direction: string,
): ListForecastMonth[] {
	if (direction === '' || column === '') {
		return list;
	} else {
		return [...list].sort((a, b) => {
			const res = compare(a[column], b[column]);
			return direction === 'asc' ? res : -res;
		});
	}
}

function matches(month: ListForecastMonth, term: string, pipe: PipeTransform) {
	return (
		month.id.toLowerCase().includes(term.toLowerCase()) ||
		month.name.toLowerCase().includes(term.toLowerCase()) ||
		month.type.toLowerCase().includes(term.toLowerCase()) ||
		month.leads_month.toLowerCase().includes(term.toLowerCase()) ||
		month.leads_needed.toLowerCase().includes(term.toLowerCase()) ||
		month.lead_cost.toLowerCase().includes(term.toLowerCase()) ||
		month.week_1.toLowerCase().includes(term.toLowerCase()) ||
		month.week_2.toLowerCase().includes(term.toLowerCase()) ||
		month.week_3.toLowerCase().includes(term.toLowerCase()) ||
		month.week_4.toLowerCase().includes(term.toLowerCase()) ||
		month.total_leads.toLowerCase().includes(term.toLowerCase()) ||
		month.diferencia.toLowerCase().includes(term.toLowerCase()) ||
		month.month_revenue.toLowerCase().includes(term.toLowerCase()) ||
		month.total_revenue.toLowerCase().includes(term.toLowerCase()) ||
		month.difference_revenue.toLowerCase().includes(term.toLowerCase())
	);
}

@Injectable({ providedIn: 'root' })
export class ListForecastServiceMonthSearch {
	private _loading$ = new BehaviorSubject<boolean>(true);
	private _search$ = new Subject<void>();
	private _list$ = new BehaviorSubject<ListForecastMonth[]>([]);
	private _total$ = new BehaviorSubject<number>(0);

	//////
	user: any;
	list: any;
	list$: ListForecastMonth[] = [];
	allRows: any;

	private _state: State = {
		page: 1,
		pageSize: 10,
		searchTerm: '',
		sortColumn: '',
		sortDirection: '',
		startIndex: 0,
		endIndex: 9,
		totalRecords: 0,
	};

	constructor(
		private pipe: DecimalPipe,
		private forecastService: ForecastService,
	) {
		this.user = getFirebaseBackend()?.getAuthenticatedUser();
	}

	get allRows$() {
		return this.allRows;
	}
	get list_result$() {
		return this._list$.asObservable();
	}
	get total$() {
		return this._total$.asObservable();
	}
	get loading$() {
		return this._loading$.asObservable();
	}
	get page() {
		return this._state.page;
	}
	get pageSize() {
		return this._state.pageSize;
	}
	get searchTerm() {
		return this._state.searchTerm;
	}
	get startIndex() {
		return this._state.startIndex;
	}
	get endIndex() {
		return this._state.endIndex;
	}
	get totalRecords() {
		return this._state.totalRecords;
	}

	set page(page: number) {
		this._set({ page });
	}
	set pageSize(pageSize: number) {
		this._set({ pageSize });
	}
	set searchTerm(searchTerm: string) {
		this._set({ searchTerm });
	}
	set sortColumn(sortColumn: SortColumn) {
		this._set({ sortColumn });
	}
	set sortDirection(sortDirection: SortDirection) {
		this._set({ sortDirection });
	}
	set startIndex(startIndex: number) {
		this._set({ startIndex });
	}
	set endIndex(endIndex: number) {
		this._set({ endIndex });
	}
	set totalRecords(totalRecords: number) {
		this._set({ totalRecords });
	}

	public listRows(
		firstDate: string,
		lastDate: string,
		selectedCampaign: number,
		selectedTypeCampaign: string,
	) {
		this.forecastService
			.listForecastMonthSearch(
				firstDate,
				lastDate,
				selectedCampaign,
				selectedTypeCampaign,
			)
			.subscribe((data) => {
				if (data.success) {
					this.list = data.body;
					this.allRows = data.body;
				}
			});
	}

	private _set(patch: Partial<State>) {
		Object.assign(this._state, patch);
		this._search$.next();
	}

	private _search(): Observable<SearchResult> {
		const { sortColumn, sortDirection, pageSize, page, searchTerm } =
			this._state;

		// 1. sort
		let list = sort(this.list, sortColumn, sortDirection);

		// 2. filter
		list = list.filter((row) => matches(row, searchTerm, this.pipe));
		const total = list.length;

		// 3. paginate
		this.totalRecords = list.length;
		this._state.startIndex = (page - 1) * this.pageSize + 1;
		this._state.endIndex = (page - 1) * this.pageSize + this.pageSize;
		if (this.endIndex > this.totalRecords) {
			this.endIndex = this.totalRecords;
		}
		list = list.slice(this._state.startIndex - 1, this._state.endIndex);
		return of({ list, total });
	}

	public SearchCampaign(
		firstDate: string,
		lastDate: string,
		selectedCampaign: number,
		selectedTypeCampaign: string,
	): any {
		this.listRows(
			firstDate,
			lastDate,
			selectedCampaign,
			selectedTypeCampaign,
		);

		setTimeout(() => {
			this._search$
				.pipe(
					tap(() => this._loading$.next(true)),
					debounceTime(200),
					switchMap(() => this._search()),
					delay(200),
					tap(() => this._loading$.next(false)),
				)
				.subscribe((result) => {
					this._list$.next(result.list);
					this._total$.next(result.total);
				});

			this._search$.next();
		}, 5000);

		return this._list$.asObservable();
	}
}
